import { useTimeline } from '@src/features/absence';
import type { VisibleRange } from '@src/features/absence';
import { useAuthentication } from '@src/features/auth';
import { useFormContext } from '@src/shared/form';
import { formatIsoDate } from '@src/shared/lib';
import { useEffect, useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import Box from '@src/shared/ui/layout/Box';
import {
  buildHolidayLookup,
  getAbsencesIdsForRange,
  getUserAbsences,
  userResourceId,
} from '../../../lib';
import type { ScheduleFormValues, WithdrawScope } from '../Form';
import TotalDatesOrHours from '../TotalDatesOrHours';
import { DatesRangeField } from './Dates';
import HoursToggle from './HoursToggle';
import NotesField from './NotesField';
import NotesHrField from './NotesHrField';
import TimeRangeField from './Time';
import TypeField from './TypeField';
import UsersField from './User';

type FormFieldsetProps = {
  disabled?: boolean;
  disableUserField?: boolean;
  /** Non-null while withdrawing: locks granularity and bounds the range. */
  withdrawScope?: WithdrawScope | null;
};

const SCROLL_LEAD_DAYS = 7;

function isWithinVisibleRange(date: Date, range: VisibleRange) {
  const t = date.getTime();
  return t >= range.start.getTime() && t < range.end.getTime();
}

function areIdsEqual(left: number[], right: number[]) {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((id, index) => id === right[index]);
}

export default function FormFieldset({
  disabled = false,
  disableUserField = false,
  withdrawScope = null,
}: FormFieldsetProps) {
  const { setValue, watch } =
    useFormContext() as UseFormReturn<ScheduleFormValues>;
  const { user: currentUser } = useAuthentication();
  const {
    absences,
    setSelectedSlot,
    selectedSlotData,
    calendar,
    isDayView,
    setSelectedUserId,
    expandAndScrollToResource,
    holidays,
    users,
    visibleRange,
  } = useTimeline();
  const holidayLookup = useMemo(() => buildHolidayLookup(holidays), [holidays]);
  const hours = withdrawScope ? withdrawScope.hourly : isDayView;
  const withdrawing = !!withdrawScope;
  const userId = watch('userId');
  const selectedUserBranchId = useMemo(
    () => users.find((user) => user.id === userId)?.branch?.id ?? null,
    [userId, users],
  );
  // Whether the person looking is the selected employee's HR manager, which
  // is the only role the HR notes exist for. The same test the calendar makes
  // elsewhere to decide what this viewer may do to this employee's days.
  const isHrOfTarget = useMemo(
    () =>
      !!userId &&
      !!currentUser?.id &&
      users.find((user) => user.id === userId)?.hrId === currentUser.id,
    [currentUser?.id, userId, users],
  );
  const [dateFrom, dateTo] = watch(['dateFrom', 'dateTo']);
  const [startTime, endTime] = watch(['startTime', 'endTime']);

  useEffect(() => {
    setSelectedSlot((slot) => {
      if (!slot) return null;
      let nextStartTime: string | null = hours ? startTime || null : null;
      let nextEndTime: string | null = hours ? endTime || null : null;

      if (hours && !nextStartTime && !nextEndTime) {
        const editedId = slot.editedAbsenceId ?? slot.editedAbsenceIds?.[0];
        const editedAbsence = editedId
          ? absences.find((a) => a.id === editedId)
          : null;

        if (
          editedAbsence &&
          !editedAbsence.allDay &&
          editedAbsence.startTime != null &&
          editedAbsence.endTime != null
        ) {
          nextStartTime = editedAbsence.startTime;
          nextEndTime = editedAbsence.endTime;
        } else {
          const slotDate = dateFrom ? formatIsoDate(dateFrom) : slot.start;
          const userAbsences = getUserAbsences(slot.userId, absences);
          const firstHourly = userAbsences
            .filter(
              (a) =>
                a.dateStr === slotDate &&
                !a.allDay &&
                a.startTime != null &&
                a.endTime != null,
            )
            .sort((a, b) =>
              (a.startTime ?? '').localeCompare(b.startTime ?? ''),
            )[0];

          if (firstHourly) {
            nextStartTime = firstHourly.startTime;
            nextEndTime = firstHourly.endTime;
          }
        }
      }
      let selectedIds: number[] = [];
      if (dateFrom && dateTo) {
        selectedIds = getAbsencesIdsForRange(
          dateFrom,
          dateTo,
          getUserAbsences(slot?.userId, absences),
          {
            dayView: hours,
            startTime: nextStartTime,
            endTime: nextEndTime,
            // Withdrawal acts on the edited absences themselves, so they
            // must stay in `selectedIds` for the range to stay highlighted.
            excludeIds: slot.withdrawing
              ? []
              : (slot.editedAbsenceIds ??
                (slot.editedAbsenceId ? [slot.editedAbsenceId] : [])),
          },
        );
      } else if (slot.start && slot.end) {
        selectedIds = getAbsencesIdsForRange(
          new Date(slot.start),
          new Date(slot.end),
          getUserAbsences(slot?.userId, absences),
          {
            dayView: hours,
            startTime: nextStartTime,
            endTime: nextEndTime,
            // Withdrawal acts on the edited absences themselves, so they
            // must stay in `selectedIds` for the range to stay highlighted.
            excludeIds: slot.withdrawing
              ? []
              : (slot.editedAbsenceIds ??
                (slot.editedAbsenceId ? [slot.editedAbsenceId] : [])),
          },
        );
      }
      const changedSlot = {
        userId,
        resourceId: userResourceId(userId),
        ...(dateFrom ? { start: formatIsoDate(dateFrom) } : {}),
        ...(dateTo ? { end: formatIsoDate(dateTo) } : {}),
        startTime: nextStartTime,
        endTime: nextEndTime,
      };
      const nextSlot = {
        ...slot,
        ...changedSlot,
        selectedIds,
      };

      if (
        slot.userId === nextSlot.userId &&
        slot.resourceId === nextSlot.resourceId &&
        slot.start === nextSlot.start &&
        slot.end === nextSlot.end &&
        slot.startTime === nextSlot.startTime &&
        slot.endTime === nextSlot.endTime &&
        slot.editedAbsenceId === nextSlot.editedAbsenceId &&
        areIdsEqual(slot.selectedIds, nextSlot.selectedIds)
      ) {
        return slot;
      }

      return nextSlot;
    });
  }, [
    absences,
    userId,
    dateFrom,
    dateTo,
    endTime,
    hours,
    setSelectedSlot,
    startTime,
  ]);

  const visibleRangeRef = useRef(visibleRange);
  visibleRangeRef.current = visibleRange;

  useEffect(() => {
    if (!dateFrom || !dateTo || hours) return;

    const range = visibleRangeRef.current;
    const fromOutside = !isWithinVisibleRange(dateFrom, range);
    const toOutside = !isWithinVisibleRange(dateTo, range);

    if (!fromOutside && !toOutside) return;

    const anchor = new Date(dateFrom);
    anchor.setDate(anchor.getDate() - SCROLL_LEAD_DAYS);
    calendar.setMonthViewFrom(anchor);
  }, [dateFrom, dateTo, calendar, hours]);

  useEffect(() => {
    setSelectedUserId(userId ?? null);
    if (userId) {
      // Delay scroll to run after selection sync updates
      // the calendar layout.
      const timer = setTimeout(() => {
        expandAndScrollToResource(userId);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [userId, setSelectedUserId, expandAndScrollToResource]);

  const slotStart = selectedSlotData?.start ?? null;
  const slotEnd = selectedSlotData?.end ?? null;
  const slotStartTime = selectedSlotData?.startTime ?? null;
  const slotEndTime = selectedSlotData?.endTime ?? null;
  const slotUserId = selectedSlotData?.userId ?? null;
  const slotTypeId = selectedSlotData?.typeId ?? null;
  const slotTentative = selectedSlotData?.tentative ?? false;
  const slotNotes = selectedSlotData?.notes ?? '';
  const slotNotesHr = selectedSlotData?.notesHr ?? '';

  // Per-field hydration: each field reacts only to its own slot field
  // changing, so the round-trip effect's slot updates only re-hydrate the
  // field whose slot value actually changed, leaving user-cleared inputs
  // untouched.
  useEffect(() => {
    if (slotStart) setValue('dateFrom', new Date(slotStart));
  }, [slotStart, setValue]);

  useEffect(() => {
    if (slotEnd) setValue('dateTo', new Date(slotEnd));
  }, [slotEnd, setValue]);

  useEffect(() => {
    setValue('startTime', slotStartTime);
  }, [slotStartTime, setValue]);

  useEffect(() => {
    setValue('endTime', slotEndTime);
  }, [slotEndTime, setValue]);

  useEffect(() => {
    setValue('userId', slotUserId);
  }, [slotUserId, setValue]);

  useEffect(() => {
    setValue('typeId', slotTypeId);
  }, [slotTypeId, setValue]);

  useEffect(() => {
    setValue('tentative', slotTentative);
  }, [slotTentative, setValue]);

  useEffect(() => {
    setValue('notes', slotNotes);
  }, [slotNotes, setValue]);

  // Cleared to `undefined` rather than to '' for anybody but HR, so the
  // payload leaves the field out altogether: the server reads a blank one as
  // an instruction to wipe whatever HR wrote.
  useEffect(() => {
    setValue('notesHr', isHrOfTarget ? slotNotesHr : undefined);
  }, [isHrOfTarget, slotNotesHr, setValue]);

  const onHoursToggle = (hours: boolean) => {
    if (hours !== null) {
      if (hours) {
        if (dateFrom && dateTo && dateFrom.getTime() !== dateTo.getTime()) {
          setValue('dateTo', dateFrom);
        }
        calendar.setDayView(dateFrom || new Date());
      } else {
        const date = dateFrom || new Date();

        calendar.setMonthView(date.getFullYear(), date.getMonth());
      }
    }
  };

  return (
    <Box sx={{ flexGrow: 1, mx: -2.5, overflow: 'auto', px: 2.5 }}>
      <UsersField disabled={disableUserField || withdrawing} />
      <HoursToggle
        disabled={disabled || withdrawing}
        hours={hours}
        onChange={onHoursToggle}
      />
      <DatesRangeField
        disabled={disabled || (withdrawing && hours)}
        hours={hours}
        minDate={withdrawScope?.minDate}
        maxDate={withdrawScope?.maxDate}
      />
      {hours && (
        <TimeRangeField
          disabled={disabled}
          minTime={withdrawScope?.minTime ?? undefined}
          maxTime={withdrawScope?.maxTime ?? undefined}
        />
      )}
      <TotalDatesOrHours
        dateFrom={dateFrom}
        dateTo={dateTo}
        startTime={startTime}
        endTime={endTime}
        hours={hours}
        isHolidayDate={(date) =>
          holidayLookup.isHolidayDate(date, selectedUserBranchId)
        }
      />
      <TypeField disabled={disabled || withdrawing} />
      <NotesField disabled={disabled} />
      {isHrOfTarget && <NotesHrField disabled={disabled} />}
    </Box>
  );
}
